import React, { useCallback, useEffect, useRef, useState } from 'react';
import Draggable from 'react-draggable';
import {
  Alert,
  Button,
  Divider,
  Input,
  InputNumber,
  List,
  message,
  Modal,
  Popconfirm,
  Progress,
  Radio,
  Select,
  Slider,
  Space,
  Spin,
  Steps,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  FileImageOutlined,
  HistoryOutlined,
  LeftOutlined,
  PlusOutlined,
  RightOutlined,
  RollbackOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { opToRect, rectToOpCoords, pxToInches, DEFAULT_RENDER_SETTINGS } from '../../utils/pdfOverlayCoords';

const { Text } = Typography;
const { Option } = Select;

const DEFAULT_SCALE = 1.5;

const MIN_SIZE_PX = 10; // 縮放時的最小寬/高（px）
const HANDLE_SIZE = 12; // 縮放手把直徑（px）

// ─────────────── Save progress modal ───────────────
const SAVE_STEPS = [
  { title: '儲存版本' },
  { title: '重新生成 PDF' },
  { title: '刷新預覽' },
];

const SaveProgressModal = ({ open, phase, blendOk, blendError }) => {
  // phase: 0=saving version, 1=re-blending, 2=refreshing/done, -1=error
  const currentStep = phase < 0 ? 2 : phase;
  const isError = phase < 0 || (!blendOk && phase >= 2 && blendError);

  return (
    <Modal
      open={open}
      footer={null}
      closable={false}
      centered
      width={400}
      title={
        <span>
          {isError
            ? <ExclamationCircleOutlined style={{ color: '#ff4d4f', marginRight: 8 }} />
            : <SaveOutlined style={{ color: '#1677ff', marginRight: 8 }} />}
          {isError ? '儲存發生問題' : '正在儲存...'}
        </span>
      }
    >
      <Steps
        current={currentStep}
        status={isError ? 'error' : phase >= 2 && blendOk ? 'finish' : 'process'}
        direction="vertical"
        size="small"
        style={{ marginTop: 16 }}
        items={SAVE_STEPS.map((s, i) => ({
          title: s.title,
          description: i === 1 && blendError && !blendOk ? (
            <Text type="danger" style={{ fontSize: 11 }}>{blendError}</Text>
          ) : i === 1 && blendOk ? '完成' : undefined,
        }))}
      />
      {!isError && phase < 2 && (
        <Progress
          percent={phase === 0 ? 33 : 66}
          status="active"
          showInfo={false}
          style={{ marginTop: 12 }}
        />
      )}
      {phase >= 2 && blendOk && (
        <div style={{ textAlign: 'center', marginTop: 16, color: '#52c41a' }}>
          <CheckCircleOutlined style={{ fontSize: 24, marginRight: 8 }} />
          <Text style={{ color: '#52c41a', fontSize: 15 }}>儲存完成，PDF 已更新</Text>
        </div>
      )}
    </Modal>
  );
};

/**
 * PDF Overlay Editor
 *
 * Props:
 *  docInstanceId {string}  - doc instance UUID
 *  onSaveSuccess {function} - callback after successful save (receives { versionId, versionNumber, blendOk })
 *  onClose {function}       - callback to dismiss the editor
 */
const PdfOverlayEditor = ({ docInstanceId, onSaveSuccess, onClose }) => {
  // ── State ──────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [operations, setOperations] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedIdx, setSelectedIdx] = useState(null);

  // Save progress: null | 0 (saving version) | 1 (blending) | 2 (done) | -1 (error)
  const [savePhase, setSavePhase] = useState(null);
  const [saveBlendOk, setSaveBlendOk] = useState(false);
  const [saveBlendError, setSaveBlendError] = useState(null);

  const [restoring, setRestoring] = useState(false);
  const [versions, setVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [rightTab, setRightTab] = useState('props');
  const [scale, setScale] = useState(DEFAULT_SCALE);
  const [renderSettings, setRenderSettings] = useState(DEFAULT_RENDER_SETTINGS);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [pageRendering, setPageRendering] = useState(false);
  const [workflowExecutionId, setWorkflowExecutionId] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const renderTaskRef = useRef(null);
  const imageFileInputRef = useRef(null);

  // ── Helpers ────────────────────────────────────────────────────────
  const authHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // ── Load overlay edit context ──────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await fetch(`/api/doc-instances/${docInstanceId}/overlay-edit`, {
          headers: authHeaders(),
          credentials: 'include',
        });
        if (!res.ok) throw new Error(await res.text() || res.statusText);
        const data = await res.json();
        if (!data.hasOverlay) {
          if (!cancelled) setError('此文件沒有可編輯的 overlay 資料（可能是舊版文件或不支援的格式）');
          return;
        }
        if (!cancelled) {
          // 同時保留 addTextBox 與 addImage：圖片 overlay 也要能在編輯器中調整位置 / 刪除，
          // 且儲存時必須一併寫回，否則 re-blend 會把圖片整個丟失。
          const ops = (data.operations || []).filter(op => op.op === 'addTextBox' || op.op === 'addImage');
          setOperations(ops);
          setWorkflowExecutionId(data.workflowExecutionId ?? null);
          // 載入後端的渲染偏移設定，讓方塊視覺位置 = 最終 PDF 文字位置
          if (data.renderSettings) {
            setRenderSettings({
              offsetXInches: data.renderSettings.offsetXInches ?? DEFAULT_RENDER_SETTINGS.offsetXInches,
              offsetYInches: data.renderSettings.offsetYInches ?? DEFAULT_RENDER_SETTINGS.offsetYInches,
              baselineLiftPoints: data.renderSettings.baselineLiftPoints ?? DEFAULT_RENDER_SETTINGS.baselineLiftPoints,
            });
          }
        }
      } catch (e) {
        if (!cancelled) setError(e.message || '載入失敗');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [docInstanceId]);

  // ── Load version history ──────────────────────────────────────────
  const loadVersions = useCallback(async () => {
    try {
      setVersionsLoading(true);
      const res = await fetch(`/api/doc-instances/${docInstanceId}/overlay-versions`, {
        headers: authHeaders(),
        credentials: 'include',
      });
      if (res.ok) setVersions(await res.json());
    } catch { /* non-critical */ }
    finally { setVersionsLoading(false); }
  }, [docInstanceId]);

  useEffect(() => { loadVersions(); }, [loadVersions]);

  const handleRestore = async (versionId) => {
    setRestoring(true);
    try {
      const res = await fetch(`/api/doc-instances/${docInstanceId}/overlay-restore/${versionId}`, {
        method: 'POST',
        headers: authHeaders(),
        credentials: 'include',
      });
      if (!res.ok) throw new Error(await res.text() || res.statusText);
      const data = await res.json();
      message.success(`已還原為 v${data.versionNumber}，請重新開啟編輯器以載入新版本`);
      onSaveSuccess?.(data);
    } catch (e) {
      message.error(`還原失敗：${e.message}`);
    } finally {
      setRestoring(false);
    }
  };

  // ── Load PDF template via pdfjs-dist ──────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const pdfjsLib = await import('pdfjs-dist');
        // Worker served from wwwroot (copied by postinstall / prebuild script)
        if (!pdfjsLib.GlobalWorkerOptions.workerSrc) {
          pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';
        }

        const token = localStorage.getItem('token');
        const url = `/api/doc-instances/${docInstanceId}/template-preview`;
        const loadingTask = pdfjsLib.getDocument({
          url,
          httpHeaders: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true,
          // 範本／疊字多為 CID-keyed 中文字型（Identity-H），沒有這兩個資源會導致中文字符缺字/空白
          cMapUrl: '/pdfjs/cmaps/',
          cMapPacked: true,
          standardFontDataUrl: '/pdfjs/standard_fonts/',
        });
        const doc = await loadingTask.promise;
        if (!cancelled) {
          setPdfDoc(doc);
          setTotalPages(doc.numPages);
          setCurrentPage(1);
        }
      } catch (e) {
        if (!cancelled) setError(`載入 PDF 範本失敗: ${e.message}`);
      }
    })();
    return () => { cancelled = true; };
  }, [docInstanceId]);

  // ── Render PDF page to canvas ──────────────────────────────────────
  useEffect(() => {
    if (!pdfDoc || !canvasRef.current) return;
    let cancelled = false;

    // 取消上一個尚未完成的 render，避免 "same canvas" 衝突
    if (renderTaskRef.current) {
      renderTaskRef.current.cancel();
      renderTaskRef.current = null;
    }

    (async () => {
      setPageRendering(true);
      try {
        const page = await pdfDoc.getPage(currentPage);
        if (cancelled) return;
        const viewport = page.getViewport({ scale });
        const canvas = canvasRef.current;
        if (!canvas || cancelled) return;
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        const task = page.render({ canvasContext: ctx, viewport });
        renderTaskRef.current = task;
        await task.promise;
        renderTaskRef.current = null;
      } catch (e) {
        // RenderingCancelledException 是主動取消的正常情況，不視為錯誤
        if (!cancelled && e?.name !== 'RenderingCancelledException') {
          setError(`渲染頁面失敗: ${e.message}`);
        }
      } finally {
        if (!cancelled) setPageRendering(false);
      }
    })();

    return () => {
      cancelled = true;
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
        renderTaskRef.current = null;
      }
    };
  }, [pdfDoc, currentPage, scale]);

  // ── Op helpers ────────────────────────────────────────────────────
  // op.page 是 0-indexed（對應後端 PdfOverlayRenderer: pageNumber = op.Page + 1）
  // currentPage 是 1-indexed（PDF.js getPage(1) = 第1頁）
  // 所以 op.page=0 → currentPage=1
  const pageOps = operations
    .map((op, idx) => ({ op, idx }))
    .filter(({ op }) => ((op.page ?? 0) + 1) === currentPage);

  const updateOp = useCallback((idx, patch) => {
    setOperations(prev => prev.map((op, i) => i === idx ? { ...op, ...patch } : op));
  }, []);

  const deleteOp = useCallback((idx) => {
    setOperations(prev => prev.filter((_, i) => i !== idx));
    setSelectedIdx(prev => {
      if (prev === idx) return null;
      if (prev !== null && prev > idx) return prev - 1;
      return prev;
    });
  }, []);

  // 手動新增一個文字方塊到當前頁面中央，並立即選取以供編輯。
  const addTextBox = useCallback(() => {
    const effOffX = renderSettings.offsetXInches ?? 0;
    const effOffY = renderSettings.offsetYInches ?? 0;
    const canvas = canvasRef.current;
    // 預設放在頁面可視中央；取不到 canvas 尺寸時退回 (1, 1) inch。
    let dispXIn = 1;
    let dispYIn = 1;
    if (canvas && canvas.width && canvas.height) {
      const pageWIn = pxToInches(canvas.width, scale);
      const pageHIn = pxToInches(canvas.height, scale);
      dispXIn = Math.max(0, pageWIn / 2 - 1);
      dispYIn = Math.max(0, pageHIn / 2 - 0.15);
    }
    const newOp = {
      op: 'addTextBox',
      page: currentPage - 1,
      x: +(dispXIn - effOffX).toFixed(4),
      y: +(dispYIn - effOffY).toFixed(4),
      w: 2,
      h: 0.3,
      text: '新文字',
      font: 'msjh',
      fontSize: 12,
      textAlign: 'left',
    };
    setSelectedIdx(operations.length); // 新元素加到陣列尾端
    setOperations(prev => [...prev, newOp]);
    setRightTab('props');
  }, [currentPage, scale, renderSettings, operations.length]);

  // 手動新增一張圖片（如簽章/蓋章印章）到當前頁面中央。
  // FIX-8330: AI 有時整批漏生成簽章圖，過去編輯器只能移動既有圖片、無法補插新圖片，
  // 導致這類缺失連存進範本記憶都補不回來。上傳走既有 workflowexecutions/{id}/media-files
  // 端點（回傳 /Uploads/Workflow_Media/{id}/... 路徑，PdfOverlayRenderer 可直接解析）。
  const triggerAddImage = useCallback(() => {
    if (!workflowExecutionId) {
      message.error('缺少所屬流程執行 ID，無法上傳圖片');
      return;
    }
    imageFileInputRef.current?.click();
  }, [workflowExecutionId]);

  const handleImageFileSelected = useCallback(async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // 允許重複選同一檔案
    if (!file) return;

    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('files', file);
      const res = await fetch(`/api/workflowexecutions/${workflowExecutionId}/media-files`, {
        method: 'POST',
        headers: authHeaders(),
        credentials: 'include',
        body: formData,
      });
      if (!res.ok) throw new Error(await res.text() || res.statusText);
      const data = await res.json();
      const uploaded = data?.data?.[0];
      if (!uploaded?.filePath) throw new Error('上傳成功但未取得檔案路徑');

      const effOffX = renderSettings.offsetXInches ?? 0;
      const effOffY = renderSettings.offsetYInches ?? 0;
      const canvas = canvasRef.current;
      const defaultW = 1.8;
      const defaultH = 1.2;
      let dispXIn = 1;
      let dispYIn = 1;
      if (canvas && canvas.width && canvas.height) {
        const pageWIn = pxToInches(canvas.width, scale);
        const pageHIn = pxToInches(canvas.height, scale);
        dispXIn = Math.max(0, pageWIn / 2 - defaultW / 2);
        dispYIn = Math.max(0, pageHIn / 2 - defaultH / 2);
      }
      const newOp = {
        op: 'addImage',
        page: currentPage - 1,
        x: +(dispXIn - effOffX).toFixed(4),
        y: +(dispYIn - effOffY).toFixed(4),
        w: defaultW,
        h: defaultH,
        imageUrl: uploaded.filePath,
      };
      setSelectedIdx(operations.length);
      setOperations(prev => [...prev, newOp]);
      setRightTab('props');
      message.success('圖片已加入，請拖曳調整位置後儲存');
    } catch (err) {
      message.error(`圖片上傳失敗：${err.message}`);
    } finally {
      setUploadingImage(false);
    }
  }, [workflowExecutionId, currentPage, scale, renderSettings, operations.length]);

  const handleDragStop = useCallback((idx, _e, data) => {
    const op = operations[idx];
    // data.x/y 是視覺顯示位置（含 renderSettings offset）
    // rectToOpCoords 會反算回 raw op.x/op.y（減去 offset）
    const coords = rectToOpCoords(
      { left: data.x, top: data.y, width: 0, height: 0 },
      scale,
      op,
      renderSettings,
    );
    updateOp(idx, { x: +coords.x.toFixed(4), y: +coords.y.toFixed(4) });
  }, [operations, scale, renderSettings, updateOp]);

  // ── Resize（拖動手把調整大小）─────────────────────────────────────
  // mousedown 在手把上 → 監聽 window mousemove/mouseup，
  // 依方向即時換算成 inch 寫回 op（含左/上邊時同步調整 x/y）。
  const handleResizeStart = useCallback((e, idx, dir) => {
    e.stopPropagation();
    e.preventDefault();
    setSelectedIdx(idx);

    const op = operations[idx];
    const startRect = opToRect(op, scale, renderSettings);
    const startMouseX = e.clientX;
    const startMouseY = e.clientY;
    const canvas = canvasRef.current;
    const boundW = canvas?.width ?? Infinity;
    const boundH = canvas?.height ?? Infinity;

    const onMove = (ev) => {
      const dx = ev.clientX - startMouseX;
      const dy = ev.clientY - startMouseY;
      let { left, top, width, height } = startRect;

      if (dir.includes('e')) width = startRect.width + dx;
      if (dir.includes('s')) height = startRect.height + dy;
      if (dir.includes('w')) {
        width = startRect.width - dx;
        left = startRect.left + dx;
      }
      if (dir.includes('n')) {
        height = startRect.height - dy;
        top = startRect.top + dy;
      }

      // 最小尺寸：往左/上拉時要反向修正位置，讓對邊固定不動
      if (width < MIN_SIZE_PX) {
        if (dir.includes('w')) left = startRect.left + startRect.width - MIN_SIZE_PX;
        width = MIN_SIZE_PX;
      }
      if (height < MIN_SIZE_PX) {
        if (dir.includes('n')) top = startRect.top + startRect.height - MIN_SIZE_PX;
        height = MIN_SIZE_PX;
      }

      // 限制在頁面（canvas）範圍內
      if (left < 0) { width += left; left = 0; }
      if (top < 0) { height += top; top = 0; }
      if (left + width > boundW) width = boundW - left;
      if (top + height > boundH) height = boundH - top;

      const coords = rectToOpCoords({ left, top, width, height }, scale, op, renderSettings);
      updateOp(idx, {
        x: +coords.x.toFixed(4),
        y: +coords.y.toFixed(4),
        w: +coords.w.toFixed(4),
        h: +coords.h.toFixed(4),
      });
    };

    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [operations, scale, renderSettings, updateOp]);

  // ── Save ──────────────────────────────────────────────────────────
  const handleSave = async () => {
    setSavePhase(0);
    setSaveBlendOk(false);
    setSaveBlendError(null);
    try {
      // Simulated phase 0 already visible; do the actual request
      const res = await fetch(`/api/doc-instances/${docInstanceId}/overlay-save`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ operations, comment: 'User edit via PdfOverlayEditor' }),
      });

      if (!res.ok) {
        const errText = await res.text();
        setSavePhase(-1);
        setSaveBlendError(errText || res.statusText);
        // Auto-dismiss error modal after 3s
        setTimeout(() => setSavePhase(null), 3000);
        message.error(`儲存失敗：${errText || res.statusText}`);
        return;
      }

      const data = await res.json();

      // Move to re-blend phase
      setSavePhase(1);
      await new Promise(r => setTimeout(r, 400)); // brief visual pause

      if (data.blendOk === false && data.blendError) {
        // Version saved but blend failed
        setSaveBlendOk(false);
        setSaveBlendError(data.blendError);
        setSavePhase(-1);
        message.warning(`版本已儲存（v${data.versionNumber}），但 PDF 重新生成失敗：${data.blendError}`);
        await loadVersions();
        setTimeout(() => setSavePhase(null), 4000);
        return;
      }

      // Phase 2: refreshing
      setSaveBlendOk(true);
      setSavePhase(2);
      await loadVersions();
      await new Promise(r => setTimeout(r, 800)); // show "完成" briefly

      setSavePhase(null);
      message.success(`已儲存並重新生成 PDF（v${data.versionNumber}）`);
      onSaveSuccess?.(data);
    } catch (e) {
      setSavePhase(-1);
      setSaveBlendError(e.message);
      setTimeout(() => setSavePhase(null), 3000);
      message.error(`儲存失敗：${e.message}`);
    }
  };

  // ── Render ────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
        <div style={{ marginTop: 12, color: '#666' }}>載入編輯器...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 24 }}>
        <Alert type="error" message={error} />
        {onClose && <Button style={{ marginTop: 12 }} onClick={onClose}>關閉</Button>}
      </div>
    );
  }

  const selectedOp = selectedIdx !== null ? operations[selectedIdx] : null;
  const isSaving = savePhase !== null;

  return (
    <div style={{ display: 'flex', height: '100%', gap: 0, background: '#f0f2f5', overflow: 'hidden' }}>
      {/* ── Save progress modal ── */}
      <SaveProgressModal
        open={isSaving}
        phase={savePhase}
        blendOk={saveBlendOk}
        blendError={saveBlendError}
      />

      {/* ── Canvas area ── */}
      <div
        ref={containerRef}
        style={{
          flex: 1,
          overflow: 'auto',
          position: 'relative',
          background: '#888',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          padding: 16,
        }}
        onClick={(e) => { if (e.target === e.currentTarget || e.target === canvasRef.current) setSelectedIdx(null); }}
      >
        <div style={{ position: 'relative', display: 'inline-block' }}>
          {pageRendering && (
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.6)', zIndex: 5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Spin />
            </div>
          )}
          <canvas ref={canvasRef} style={{ display: 'block', boxShadow: '0 2px 16px rgba(0,0,0,0.3)' }} />

          {/* Overlay labels */}
          {pageOps.map(({ op, idx }) => {
            const rect = opToRect(op, scale, renderSettings);
            const isSelected = selectedIdx === idx;
            const isImage = op.op === 'addImage';
            return (
              <Draggable
                key={idx}
                position={{ x: rect.left, y: rect.top }}
                onStop={(e, data) => handleDragStop(idx, e, data)}
                bounds="parent"
                cancel=".overlay-delete-btn"
              >
                <div
                  onClick={(e) => { e.stopPropagation(); setSelectedIdx(idx); }}
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    width: rect.width,
                    height: Math.max(rect.height, isImage ? 24 : 16),
                    boxSizing: 'border-box',
                    border: isSelected
                      ? '2px solid #1677ff'
                      : isImage
                        ? '1px dashed rgba(82,196,26,0.7)'
                        : '1px dashed rgba(22,119,255,0.6)',
                    background: isSelected
                      ? 'rgba(22,119,255,0.08)'
                      : isImage
                        ? 'rgba(82,196,26,0.08)'
                        : 'rgba(255,255,200,0.35)',
                    cursor: 'move',
                    display: 'flex',
                    alignItems: 'center',
                    overflow: 'hidden',
                    borderRadius: 2,
                    userSelect: 'none',
                    zIndex: isSelected ? 20 : 10,
                  }}
                >
                  {isImage ? (
                    op.imageUrl ? (
                      <img
                        src={op.imageUrl}
                        alt="overlay"
                        draggable={false}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          pointerEvents: 'none',
                        }}
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      />
                    ) : (
                      <span style={{
                        flex: 1,
                        textAlign: 'center',
                        color: '#52c41a',
                        fontSize: Math.max(9, 11 * scale * 0.6),
                        pointerEvents: 'none',
                      }}>
                        <FileImageOutlined /> 圖片
                      </span>
                    )
                  ) : (
                    <span style={{
                      flex: 1,
                      fontSize: op.fontSize
                        ? Math.max(6, op.fontSize * scale)
                        : Math.max(8, 10 * scale * 0.85),
                      color: '#1a1a1a',
                      padding: '0 2px',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      fontFamily: opFontCss(op.font),
                      textAlign: op.textAlign ?? 'left',
                      pointerEvents: 'none',
                      lineHeight: 1.2,
                    }}>
                      {op.text || '(空)'}
                    </span>
                  )}
                  {/* Delete X（選取時顯示） */}
                  {isSelected && (
                    <Popconfirm
                      title="刪除此元素？"
                      onConfirm={(e) => { e?.stopPropagation(); deleteOp(idx); }}
                      okText="刪除"
                      cancelText="取消"
                      okButtonProps={{ danger: true }}
                    >
                      <span
                        className="overlay-delete-btn"
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          position: 'absolute',
                          top: 1,
                          right: 1,
                          width: 16,
                          height: 16,
                          background: '#ff4d4f',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          flexShrink: 0,
                          pointerEvents: 'all',
                          zIndex: 30,
                          boxShadow: '0 0 0 1px #fff',
                        }}
                      >
                        <CloseOutlined style={{ fontSize: 9, color: '#fff' }} />
                      </span>
                    </Popconfirm>
                  )}
                </div>
              </Draggable>
            );
          })}

          {/* ── Resize handles（canvas wrapper 層，不受 overflow:hidden 影響）──
               只在有選取元素且屬於當前頁面時渲染 */}
          {selectedIdx !== null && (() => {
            const entry = pageOps.find(({ idx }) => idx === selectedIdx);
            if (!entry) return null;
            const selRect = opToRect(entry.op, scale, renderSettings);
            const boxH = Math.max(selRect.height, entry.op.op === 'addImage' ? 24 : 16);
            const half = HANDLE_SIZE / 2;
            // 8 個手把的座標（手把中心對齊四角與四邊中點）
            const handles = [
              { dir: 'nw', cursor: 'nwse-resize', x: selRect.left - half,                          y: selRect.top - half },
              { dir: 'n',  cursor: 'ns-resize',   x: selRect.left + selRect.width / 2 - half,      y: selRect.top - half },
              { dir: 'ne', cursor: 'nesw-resize', x: selRect.left + selRect.width - half,          y: selRect.top - half },
              { dir: 'e',  cursor: 'ew-resize',   x: selRect.left + selRect.width - half,          y: selRect.top + boxH / 2 - half },
              { dir: 'se', cursor: 'nwse-resize', x: selRect.left + selRect.width - half,          y: selRect.top + boxH - half },
              { dir: 's',  cursor: 'ns-resize',   x: selRect.left + selRect.width / 2 - half,      y: selRect.top + boxH - half },
              { dir: 'sw', cursor: 'nesw-resize', x: selRect.left - half,                          y: selRect.top + boxH - half },
              { dir: 'w',  cursor: 'ew-resize',   x: selRect.left - half,                          y: selRect.top + boxH / 2 - half },
            ];
            return handles.map(({ dir, cursor, x, y }) => (
              <span
                key={`rh-${dir}`}
                className="overlay-resize-handle"
                onMouseDown={(e) => handleResizeStart(e, selectedIdx, dir)}
                onClick={(e) => e.stopPropagation()}
                style={{
                  position: 'absolute',
                  left: x,
                  top: y,
                  width: HANDLE_SIZE,
                  height: HANDLE_SIZE,
                  background: '#1677ff',
                  border: '2px solid #fff',
                  borderRadius: '50%',
                  boxSizing: 'border-box',
                  boxShadow: '0 0 3px rgba(0,0,0,0.4)',
                  cursor,
                  zIndex: 50,
                  pointerEvents: 'all',
                }}
              />
            ));
          })()}
        </div>
      </div>

      {/* ── Right panel ── */}
      <div style={{
        width: 280,
        background: '#fff',
        borderLeft: '1px solid #e8e8e8',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}>
        {/* Toolbar */}
        <div style={{ padding: '12px 16px', borderBottom: '1px solid #e8e8e8', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={isSaving}
            onClick={handleSave}
            size="small"
          >
            儲存
          </Button>
          <Button
            icon={<PlusOutlined />}
            onClick={addTextBox}
            size="small"
            disabled={isSaving}
          >
            新增文字
          </Button>
          <Button
            icon={<FileImageOutlined />}
            onClick={triggerAddImage}
            size="small"
            loading={uploadingImage}
            disabled={isSaving || !workflowExecutionId}
          >
            新增圖片
          </Button>
          <input
            ref={imageFileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleImageFileSelected}
          />
          {onClose && (
            <Button icon={<CloseOutlined />} onClick={onClose} size="small" disabled={isSaving}>
              關閉
            </Button>
          )}
        </div>

        {/* Page navigation */}
        <div style={{ padding: '8px 16px', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Button
            size="small"
            icon={<LeftOutlined />}
            disabled={currentPage <= 1}
            onClick={() => { setCurrentPage(p => p - 1); setSelectedIdx(null); }}
          />
          <Text style={{ flex: 1, textAlign: 'center' }}>{currentPage} / {totalPages}</Text>
          <Button
            size="small"
            icon={<RightOutlined />}
            disabled={currentPage >= totalPages}
            onClick={() => { setCurrentPage(p => p + 1); setSelectedIdx(null); }}
          />
        </div>

        {/* Zoom */}
        <div style={{ padding: '8px 16px', borderBottom: '1px solid #f0f0f0' }}>
          <Text type="secondary" style={{ fontSize: 11 }}>縮放</Text>
          <Slider
            min={0.5}
            max={3}
            step={0.25}
            value={scale}
            onChange={setScale}
            style={{ margin: '4px 0 0' }}
          />
        </div>

        {/* Tabbed properties / history panel */}
        {/* antd Tabs 的 content-holder 預設不受 flex 高度約束，需用 CSS 讓其可捲動 */}
        <style>{`
          .pdf-overlay-right-tabs { min-height: 0; }
          .pdf-overlay-right-tabs .ant-tabs-content-holder { flex: 1; min-height: 0; overflow-y: auto; }
          .pdf-overlay-right-tabs .ant-tabs-content { height: 100%; }
        `}</style>
        <Tabs
          className="pdf-overlay-right-tabs"
          activeKey={rightTab}
          onChange={setRightTab}
          size="small"
          style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}
          tabBarStyle={{ padding: '0 16px', marginBottom: 0 }}
          items={[
            {
              key: 'props',
              label: '屬性',
              children: (
                <div style={{ flex: 1, overflow: 'auto', padding: '8px 16px' }}>
                  {selectedOp ? (
                    <>
                      <PropertiesPanel
                        op={selectedOp}
                        idx={selectedIdx}
                        renderSettings={renderSettings}
                        onChange={(patch) => updateOp(selectedIdx, patch)}
                        onDelete={() => deleteOp(selectedIdx)}
                      />
                    </>
                  ) : (
                    <div style={{ color: '#aaa', textAlign: 'center', marginTop: 32, fontSize: 13 }}>
                      點擊標籤以編輯屬性
                    </div>
                  )}

                  <Divider style={{ margin: '12px 0 8px' }} />
                  <Text type="secondary" style={{ fontSize: 11 }}>本頁元素（{pageOps.length}）</Text>
                  {pageOps.map(({ op, idx }) => (
                    <div
                      key={idx}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '4px 8px',
                        marginTop: 4,
                        borderRadius: 4,
                        cursor: 'pointer',
                        background: selectedIdx === idx ? '#e6f4ff' : 'transparent',
                        border: selectedIdx === idx ? '1px solid #91caff' : '1px solid transparent',
                        fontSize: 12,
                      }}
                      onClick={() => setSelectedIdx(idx)}
                    >
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {op.op === 'addImage'
                          ? <span style={{ color: '#52c41a' }}><FileImageOutlined style={{ marginRight: 4 }} />圖片</span>
                          : (op.text || '(空)')}
                      </span>
                      <Popconfirm
                        title="刪除此元素？"
                        onConfirm={(e) => { e?.stopPropagation(); deleteOp(idx); }}
                        okText="刪除"
                        cancelText="取消"
                        okButtonProps={{ danger: true }}
                      >
                        <Button
                          type="text"
                          size="small"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={(e) => e.stopPropagation()}
                          style={{ flexShrink: 0, opacity: 0.6 }}
                        />
                      </Popconfirm>
                    </div>
                  ))}
                </div>
              ),
            },
            {
              key: 'history',
              label: (
                <span><HistoryOutlined style={{ marginRight: 4 }} />版本</span>
              ),
              children: (
                <div style={{ padding: '8px 16px', overflow: 'auto', height: '100%' }}>
                  {versionsLoading ? (
                    <Spin size="small" />
                  ) : versions.length === 0 ? (
                    <Text type="secondary" style={{ fontSize: 12 }}>尚無版本記錄</Text>
                  ) : (
                    <List
                      size="small"
                      dataSource={[...versions].reverse()}
                      renderItem={(v) => (
                        <List.Item
                          style={{ padding: '6px 0' }}
                          actions={[
                            <Popconfirm
                              title={`還原到 v${v.versionNumber}？`}
                              onConfirm={() => handleRestore(v.id)}
                              okText="確認"
                              cancelText="取消"
                            >
                              <Button
                                type="link"
                                size="small"
                                icon={<RollbackOutlined />}
                                loading={restoring}
                              >
                                還原
                              </Button>
                            </Popconfirm>
                          ]}
                        >
                          <div style={{ fontSize: 12, lineHeight: 1.4 }}>
                            <div>
                              <Tag color={v.createdBySource === 'ai_generated' ? 'geekblue' : 'green'} style={{ fontSize: 10 }}>
                                v{v.versionNumber} {v.createdBySource === 'ai_generated' ? 'AI' : '編輯'}
                              </Tag>
                            </div>
                            <div style={{ color: '#999', fontSize: 11 }}>
                              <ClockCircleOutlined style={{ marginRight: 4 }} />
                              {new Date(v.createdAt).toLocaleString('zh-TW', { hour12: false })}
                            </div>
                            {v.comment && <div style={{ color: '#666', fontSize: 11, marginTop: 2 }}>{v.comment}</div>}
                          </div>
                        </List.Item>
                      )}
                    />
                  )}
                </div>
              ),
            },
          ]}
        />
      </div>
    </div>
  );
};

// ─────────────────── Properties Panel ───────────────────────────────

const FONT_OPTIONS = [
  { label: '微軟正黑 (msjh)', value: 'msjh' },
  { label: '細明體 (mingliu)', value: 'mingliu' },
  { label: '新細明體 (pmingliu)', value: 'pmingliu' },
  { label: 'Arial', value: 'arial' },
  { label: 'Times New Roman', value: 'times new roman' },
];

/** 後端字體別名 → CSS font-family（用於 canvas 即時預覽） */
const FONT_CSS_MAP = {
  msjh:              '"Microsoft JhengHei", "微軟正黑體", sans-serif',
  mingliu:           '"MingLiU", "細明體", serif',
  pmingliu:          '"PMingLiU", "新細明體", serif',
  arial:             'Arial, sans-serif',
  'times new roman': '"Times New Roman", Times, serif',
};

function opFontCss(fontName) {
  if (!fontName) return '"Microsoft JhengHei", "微軟正黑體", sans-serif';
  const key = fontName.trim().toLowerCase();
  return FONT_CSS_MAP[key] ?? fontName;
}

const PropertiesPanel = ({ op, idx, renderSettings, onChange, onDelete }) => {
  const rs = renderSettings ?? DEFAULT_RENDER_SETTINGS;
  const effOffX = (op.offsetX != null ? op.offsetX : rs.offsetXInches) ?? 0;
  const effOffY = (op.offsetY != null ? op.offsetY : rs.offsetYInches) ?? 0;
  const isImage = op.op === 'addImage';

  // 視覺位置（顯示給使用者）= raw + offset
  const dispX = op.x != null ? +(op.x + effOffX).toFixed(4) : undefined;
  const dispY = op.y != null ? +(op.y + effOffY).toFixed(4) : undefined;

  return (
    <Space direction="vertical" style={{ width: '100%' }} size={12}>
      {/* Delete button at top of panel */}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Popconfirm
          title="刪除此元素？此操作在儲存前可以復原（重新載入）"
          onConfirm={onDelete}
          okText="刪除"
          cancelText="取消"
          okButtonProps={{ danger: true }}
        >
          <Button
            danger
            size="small"
            icon={<DeleteOutlined />}
          >
            刪除元素
          </Button>
        </Popconfirm>
      </div>

      {isImage ? (
        <div>
          <Text type="secondary" style={{ fontSize: 11 }}>
            <FileImageOutlined style={{ marginRight: 4 }} />圖片
          </Text>
          {op.imageUrl ? (
            <div style={{
              marginTop: 4,
              padding: 8,
              border: '1px solid #f0f0f0',
              borderRadius: 4,
              textAlign: 'center',
              background: '#fafafa',
            }}>
              <img
                src={op.imageUrl}
                alt="overlay"
                style={{ maxWidth: '100%', maxHeight: 120, objectFit: 'contain' }}
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
              <div style={{ fontSize: 10, color: '#999', wordBreak: 'break-all', marginTop: 4 }}>
                {op.imageUrl}
              </div>
            </div>
          ) : (
            <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>（無圖片來源）</div>
          )}
        </div>
      ) : (
        <>
          <div>
            <Text type="secondary" style={{ fontSize: 11 }}>文字內容</Text>
            <Input.TextArea
              value={op.text ?? ''}
              rows={2}
              onChange={(e) => onChange({ text: e.target.value })}
              style={{ marginTop: 4 }}
            />
          </div>

          <div>
            <Text type="secondary" style={{ fontSize: 11 }}>字型</Text>
            <Select
              value={op.font ?? 'msjh'}
              style={{ width: '100%', marginTop: 4 }}
              onChange={(v) => onChange({ font: v })}
              options={FONT_OPTIONS.map(f => ({ label: f.label, value: f.value }))}
              showSearch
              filterOption={(input, opt) => (opt?.label ?? '').toLowerCase().includes(input.toLowerCase())}
            />
          </div>

          <div>
            <Text type="secondary" style={{ fontSize: 11 }}>字級（pt）</Text>
            <InputNumber
              value={op.fontSize ?? null}
              min={4}
              max={72}
              step={0.5}
              placeholder="自動"
              style={{ width: '100%', marginTop: 4 }}
              onChange={(v) => onChange({ fontSize: v ?? undefined })}
            />
          </div>

          <div>
            <Text type="secondary" style={{ fontSize: 11 }}>水平對齊</Text>
            <Radio.Group
              value={op.textAlign ?? 'left'}
              onChange={(e) => onChange({ textAlign: e.target.value })}
              style={{ marginTop: 4 }}
              optionType="button"
              buttonStyle="solid"
              size="small"
            >
              <Radio.Button value="left">左</Radio.Button>
              <Radio.Button value="center">中</Radio.Button>
              <Radio.Button value="right">右</Radio.Button>
            </Radio.Group>
          </div>

          <div>
            <Text type="secondary" style={{ fontSize: 11 }}>垂直對齊</Text>
            <Radio.Group
              value={op.vAlign ?? ''}
              onChange={(e) => onChange({ vAlign: e.target.value || undefined })}
              style={{ marginTop: 4 }}
              optionType="button"
              buttonStyle="solid"
              size="small"
            >
              <Radio.Button value="">預設</Radio.Button>
              <Radio.Button value="top">頂</Radio.Button>
              <Radio.Button value="middle">中</Radio.Button>
              <Radio.Button value="bottom">底</Radio.Button>
            </Radio.Group>
          </div>
        </>
      )}

      <Divider style={{ margin: '4px 0' }} />

      {/* X/Y 顯示視覺位置（= raw + offset），W/H 顯示原始值 */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <div>
          <Text type="secondary" style={{ fontSize: 11 }}>X 位置 (inch)</Text>
          <InputNumber
            value={dispX}
            step={0.005}
            precision={4}
            style={{ width: '100%', marginTop: 2 }}
            onChange={(v) => {
              if (v != null) onChange({ x: +(v - effOffX).toFixed(4) });
            }}
          />
        </div>
        <div>
          <Text type="secondary" style={{ fontSize: 11 }}>Y 位置 (inch)</Text>
          <InputNumber
            value={dispY}
            step={0.005}
            precision={4}
            style={{ width: '100%', marginTop: 2 }}
            onChange={(v) => {
              if (v != null) onChange({ y: +(v - effOffY).toFixed(4) });
            }}
          />
        </div>
        {['w', 'h'].map((field) => (
          <div key={field}>
            <Text type="secondary" style={{ fontSize: 11 }}>{field.toUpperCase()} 尺寸 (inch)</Text>
            <InputNumber
              value={op[field] != null ? +op[field].toFixed(4) : undefined}
              step={0.005}
              precision={4}
              style={{ width: '100%', marginTop: 2 }}
              onChange={(v) => onChange({ [field]: v ?? op[field] })}
            />
          </div>
        ))}
      </div>
    </Space>
  );
};

export default PdfOverlayEditor;
